import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, ExternalLink, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useDeployment } from '@/hooks/queries/useDeployment';
import { useProject } from '@/hooks/queries/useProject';
import { useBuildLogs } from '@/hooks/queries/useBuildLogs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LoadingState from '@/components/common/LoadingState';
import { formatDistanceToNow } from 'date-fns';

export default function DeploymentDetails() {
  const { projectId, deploymentId } = useParams();
  const navigate = useNavigate();
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [stopPollingLogs, setStopPollingLogs] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  // Enable polling to get real-time updates
  const { data: deployment, isLoading: isLoadingDeployment } = useDeployment(
    projectId, 
    deploymentId, 
    true // Enable polling
  );
  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  
  // Fetch build logs - poll while building/pending, or for 1 minute after completion
  const isBuilding = deployment?.status === 'building' || deployment?.status === 'pending';
  const shouldPollLogs = (isBuilding || !stopPollingLogs) && !!deploymentId;
  const { data: buildLogs = [], error: logsError } = useBuildLogs(deploymentId, shouldPollLogs);

  // Stop polling logs 1 minute after build completes
  useEffect(() => {
    if (deployment?.status === 'success' || deployment?.status === 'failed') {
      const timer = setTimeout(() => {
        console.log('[DeploymentDetails] Stopping log polling after 1 minute');
        setStopPollingLogs(true);
      }, 60000); // 1 minute
      
      return () => clearTimeout(timer);
    }
  }, [deployment?.status]);

  // Debug logs
  useEffect(() => {
    console.log('[DeploymentDetails] Deployment:', deployment?.status);
    console.log('[DeploymentDetails] Build logs count:', buildLogs.length);
    console.log('[DeploymentDetails] Polling logs:', shouldPollLogs);
  }, [deployment?.status, buildLogs.length, shouldPollLogs]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (buildLogs.length > 0 && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [buildLogs]);

  // Auto-redirect to project page after successful deployment
  useEffect(() => {
    if (deployment?.status === 'success' && !hasAutoOpened) {
      console.log('[DeploymentDetails] Deployment successful! Redirecting to project page in 3 seconds...');
      setHasAutoOpened(true);
      
      // Show success message for 3 seconds, then redirect to project page
      const timer = setTimeout(() => {
        navigate(`/projects/${projectId}`, { replace: true });
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [deployment?.status, hasAutoOpened, navigate, projectId]);

  const getLogColor = (log: { level: string; source: string }) => {
    // Error takes priority
    if (log.level === 'error') return 'text-red-400';
    if (log.level === 'warn') return 'text-yellow-400';
    
    // Color by source
    switch (log.source) {
      case 'system': return 'text-blue-400';
      case 'git': return 'text-green-400';
      case 'npm': return 'text-orange-400';
      case 'build': return 'text-purple-400';
      case 'deploy': return 'text-cyan-400';
      default: return 'text-gray-300';
    }
  };

  const getStatusBadge = () => {
    if (!deployment) return null;

    switch (deployment.status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      case 'building':
        return (
          <Badge className="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            Building
          </Badge>
        );
      case 'success':
        return (
          <Badge className="flex items-center gap-1 bg-green-500 hover:bg-green-600">
            <CheckCircle className="h-3 w-3" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge>{deployment.status}</Badge>;
    }
  };

  const openPreview = () => {
    if (deployment?.preview_url) {
      // Check if URL already has protocol
      const url = deployment.preview_url.startsWith('http://') || 
                  deployment.preview_url.startsWith('https://')
        ? deployment.preview_url
        : `http://${deployment.preview_url}`;
      window.open(url, '_blank');
    }
  };

  if (isLoadingDeployment || isLoadingProject) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <LoadingState />
        <p className="text-center text-gray-500 mt-4">
          Loading deployment details...
        </p>
        <p className="text-center text-gray-400 text-sm mt-2">
          Project ID: {projectId} | Deployment ID: {deploymentId}
        </p>
      </div>
    );
  }

  if (!deployment || !project) {
    return (
      <div className="text-center py-12 max-w-4xl mx-auto">
        <p className="text-red-600 mb-4">Deployment or Project not found</p>
        <p className="text-gray-500 text-sm mb-4">
          Project ID: {projectId} | Deployment ID: {deploymentId}
        </p>
        <Button onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </div>
    );
  }

  const isInProgress = deployment.status === 'pending' || deployment.status === 'building';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Deployment Details</h1>
            <p className="text-gray-600 mt-1">{project.name}</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      {/* Status Banner */}
      {isInProgress && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-900">
                  {deployment.status === 'pending' ? 'Deployment Queued' : 'Build in Progress'}
                </p>
                <p className="text-sm text-yellow-700">
                  {deployment.status === 'pending' 
                    ? 'Your deployment is waiting to start...' 
                    : 'Building your project... This usually takes a few moments.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Banner with Auto-Redirect Notice */}
      {deployment.status === 'success' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Deployment Successful!</p>
                  <p className="text-sm text-green-700">
                    Redirecting to home in 3 seconds...
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/')} variant="outline">
                Go to Home Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed Banner */}
      {deployment.status === 'failed' && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">Deployment Failed</p>
                <p className="text-sm text-red-700">
                  {deployment.error_message || 'An error occurred during the build process'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deployment Information */}
      <Card>
        <CardHeader>
          <CardTitle>Deployment Information</CardTitle>
          <CardDescription>Details about this deployment</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Deployment ID:</span>
              <div className="font-mono text-xs mt-1 bg-gray-100 px-2 py-1 rounded break-all">
                {deployment.id}
              </div>
            </div>
            <div>
              <span className="text-gray-600">Status:</span>
              <div className="mt-1">{getStatusBadge()}</div>
            </div>
            <div>
              <span className="text-gray-600">Branch:</span>
              <div className="font-medium mt-1">{deployment.branch || 'N/A'}</div>
            </div>
            <div>
              <span className="text-gray-600">Commit SHA:</span>
              <div className="font-mono text-xs mt-1 bg-gray-100 px-2 py-1 rounded">
                {deployment.commit_sha?.substring(0, 8) || 'N/A'}
              </div>
            </div>
            <div>
              <span className="text-gray-600">Created:</span>
              <div className="font-medium mt-1">
                {formatDistanceToNow(new Date(deployment.created_at), { addSuffix: true })}
              </div>
            </div>
            {deployment.build_time_seconds && (
              <div>
                <span className="text-gray-600">Build Time:</span>
                <div className="font-medium mt-1">{deployment.build_time_seconds}s</div>
              </div>
            )}
          </div>

          {deployment.preview_url && (
            <div className="pt-4 border-t">
              <span className="text-gray-600 text-sm">Preview URL:</span>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm break-all">
                  {deployment.preview_url}
                </code>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openPreview}
                  disabled={deployment.status !== 'success'}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Build Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className={`h-4 w-4 ${isBuilding ? 'animate-spin' : ''}`} />
            Build Logs
          </CardTitle>
          <CardDescription>
            {isBuilding 
              ? 'Live output from the build process (updates every 3 seconds)' 
              : 'Build process output'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logsError && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Unable to fetch build logs. The backend logs endpoint may not be implemented yet.
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                API Error: {logsError instanceof Error ? logsError.message : 'Unknown error'}
              </p>
            </div>
          )}
          <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-lg h-96 overflow-y-auto">
            {buildLogs.length === 0 ? (
              <div className="text-gray-500 italic space-y-2">
                {logsError ? (
                  <>
                    <div>❌ Build logs endpoint not available</div>
                    <div className="text-yellow-500">Backend needs to implement: GET /builds/{deploymentId}/logs</div>
                    <div className="text-gray-600 mt-4">Expected response format:</div>
                    <div className="text-blue-400">{'{'}</div>
                    <div className="text-blue-400 ml-4">"data": [</div>
                    <div className="text-blue-400 ml-8">{'{'}</div>
                    <div className="text-blue-400 ml-12">"timestamp": "2024-12-26T14:32:15Z",</div>
                    <div className="text-blue-400 ml-12">"message": "Cloning repository...",</div>
                    <div className="text-blue-400 ml-12">"deployment_id": "deploy_123"</div>
                    <div className="text-blue-400 ml-8">{'}'}</div>
                    <div className="text-blue-400 ml-4">]</div>
                    <div className="text-blue-400">{'}'}</div>
                  </>
                ) : isBuilding ? (
                  'Waiting for logs...'
                ) : (
                  'No logs available'
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {buildLogs.map((log, index) => (
                  <div key={log.id || index} className="flex gap-2">
                    <span className="text-gray-500 select-none shrink-0">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <span className={`text-gray-400 select-none shrink-0 w-16 ${log.source ? '' : 'hidden'}`}>
                      [{log.source}]
                    </span>
                    <span className={`break-all ${getLogColor(log)}`}>
                      {log.message}
                    </span>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </div>
            )}
          </div>
          {isBuilding && !logsError && (
            <p className="text-xs text-gray-500 mt-2">
              <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
              Fetching new logs...
            </p>
          )}
        </CardContent>
      </Card>

      {/* Project Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Project Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Project Name:</span>
            <span className="font-medium">{project.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Repository:</span>
            <a
              href={project.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              {project.github_url.split('/').slice(-2).join('/')}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Project ID:</span>
            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{project.id}</code>
          </div>
        </CardContent>
      </Card>

      {/* Build Progress Animation */}
      {isInProgress && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Build Progress</span>
                <span className="text-gray-600">In Progress...</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="bg-blue-600 h-full rounded-full animate-pulse" style={{ width: '60%' }} />
              </div>
              <p className="text-xs text-gray-600">
                Polling for updates every 2 seconds...
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
